import { useEffect, useMemo, useState } from 'react';

type StoredDraft<T> = {
  value: T;
  updatedAt: number;
};

export type UseDraftOptions = {
  /**
   * Prefix for the localStorage key. Defaults to 'draft'.
   */
  prefix?: string;
};

/**
 * Lightweight localStorage-backed draft helper to avoid losing in-progress input.
 * Uses a stable key built from provided parts; when the key changes it will hydrate
 * from storage (if present) or fall back to the given initialValue.
 */
export function useDraft<T>(
  keyParts: Array<string | number | undefined | null>,
  initialValue: T,
  options?: UseDraftOptions,
) {
  const prefix = options?.prefix ?? 'draft';

  const storageKey = useMemo(
    () =>
      `${prefix}:${keyParts
        .filter((part) => part !== undefined && part !== null && String(part).length > 0)
        .join('|')}`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prefix, ...keyParts],
  );

  const [value, setValue] = useState<T>(initialValue);

  // Hydrate when key/initialValue changes
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as StoredDraft<T> | null;
        if (parsed && 'value' in parsed) {
          setValue(parsed.value);
          return;
        }
      }
    } catch {
      // ignore malformed storage
    }
    setValue(initialValue);
  }, [storageKey, initialValue]);

  const persist = (next: T) => {
    setValue(next);
    try {
      const payload: StoredDraft<T> = { value: next, updatedAt: Date.now() };
      localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch {
      // Best-effort; if storage fails, we still keep state in memory
    }
  };

  const clear = () => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
    setValue(initialValue);
  };

  return {
    value,
    setValue: persist,
    clear,
  };
}

/**
 * @deprecated Use `useDraft` instead. This is kept for backwards compatibility.
 */
export const useExerciseDraft = <T>(
  keyParts: Array<string | number | undefined | null>,
  initialValue: T,
) => useDraft(keyParts, initialValue, { prefix: 'p2e-exercise-draft' });
